import {
  Button,
  CIActionBtnGroup,
  CIActionGuard,
  CIAppliedFilter,
  CIAppliedListFilter,
  CIStatusCapsule,
  CITableGrid,
  CITooltip,
  getLocaleDateTimeFormat,
  Loader,
  NoDataAvailable,
  SearchInput,
  toLocalDate,
  UnableToFetch,
  useUserPermission,
  usePaginationPreference
} from 'ccs-ui-components';
import debounce from 'lodash.debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import DropdownMenuButton from '../../../../../common/components/UI/menu/DropdownMenuButton';
import { requiredAuthPermission } from '../../../../../common/constants/UserPermissionConstants';
import CircleBadge from '../../../../_components/badges/CircleBadge';
import CapsuleButton from '../../../../_components/capsule-button/CapsuleButton';
import RegistriesFilter from '../../../../_components/registries-filter/RegistriesFilter';
import { allStatusList, APPPLIED_FILTER, STATUS_TYPES, URL_REGISTRY } from '../../../../constants';
import { ISourceIdentifierComp } from '../../../../models/sources/IRegistriesListModel';
import { fetchSourceRegistryListRequest } from '../../../../store/actions/sagaActions/schedule-maintenance/GetAllSourceRegistriesSagaActions';
import { ISourceData } from '../../../../store/actions/types/sources/GetAllSourceRegistries';

import { REGISTRY_TYPE } from '../../../../../dashboard/constants/CommonConstants';
import { AppDispatch, useSelector } from '../../../../store/AppStore';
import { addFilterSourceRegistryData, addSortSourceRegistryData, ISourceRegistryFilterState } from '../../../../store/reducer/sources/SourceRegistryFilterReducer';
import { registryListSchema } from '../../../../utilities/sources/RegistriesSchemaDefintion';
import { defaultSourceListStatus, defaultSourceListStatusArray, getFilterParams, queryParamModifier } from '../../../../utilities/sources/SourceRegistryUtil';
import classes from './SourceList.module.scss';

export interface ISourceListQueryParams extends ISourceRegistryFilterState {
  page?: string;
  item?: string;
}

const SourceList = () => {
  const { t: translate } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { permissionObject } = useUserPermission();
  const { getPreferredPageSize, persistPageSize } = usePaginationPreference();
  const { sourceData, isLoading, listSourceRegistryErrorMessage } = useSelector(
    (state) => state.sourceRegistryListReducer
  );

  const route = useLocation();
  const navigate = useNavigate();
  const debounceDelay = 300;

  const { searchText: reduxSearch, status: reduxStatus, fromDate: reduxFromDate, toDate: reduxToDate, sort: reduxSort, sortType: reduxSortType } = useSelector(
    (state) => state.filterSourceRegistryReducer
  );

  const params = useMemo(
    () => getFilterParams(route.search, { searchText: reduxSearch, status: reduxStatus, fromDate: reduxFromDate, toDate: reduxToDate, sort: reduxSort, sortType: reduxSortType }),
    [route.search, reduxSearch, reduxStatus, reduxFromDate, reduxToDate, reduxSort, reduxSortType]
  );

  const [queryParams, setQueryParams] = useState<ISourceListQueryParams>(
    params
  );
  const [page, setPage] = useState<number>(sourceData.currentPageNumber);
  const [item, setItem] = useState<number>(params.item ? Number(params.item) : getPreferredPageSize());
  const [selectedSortField, setSelectedSortField] = useState(queryParams.sort ?? 'updatedTimestamp');
  const [selectedSortFieldOrder, setSelectedSortFieldOrder] = useState(!!((queryParams.sortType && queryParams.sortType === 'ASC')));
  const [searchTemp, setSearchTemp] = useState<string>(queryParams.searchText ?? '');
  const sortedObj = { sortedName: selectedSortField, sortedOrder: queryParams.sortType };
  const renderListCondition = !listSourceRegistryErrorMessage && sourceData.sources?.length !== 0;
  const allStatusMap: { [key: string]: string } = {};
  allStatusList.forEach((s) => { allStatusMap[s.value] = s.label; });
  const history = useNavigate();

  /**
   * @method onGetPageHandler
   * @description Handles the event when a page number is selected.
   * @param {number} pageNumber - The selected page number.
   */
  const onGetPageHandler = (pageNumber: number) => {
    setPage(pageNumber);
    setQueryParams((pre: ISourceListQueryParams) => ({
      ...pre,
      page: String(pageNumber),
    }));
  };

  /**
   * @method onGetItemsHandler
   * @description Handles the event when an item is selected.
   * @param itemNumber - The number of the selected item.
   */
  const onGetItemsHandler = (itemNumber: number) => {
    setItem(itemNumber);
    setQueryParams((pre: ISourceListQueryParams) => ({
      ...pre,
      item: String(itemNumber),
    }));
  };

  /**
  * @method resetFiltersHandler
  * @description Resets the filters and the page index
  */
  const resetFiltersHandler = () => {
    setPage(0);
    setQueryParams((pre): ISourceListQueryParams => ({
      ...pre,
      status: defaultSourceListStatus,
      fromDate: '',
      toDate: '',
      page: '0'
    }));
  };

  /**
   * @method setFilterStates
   * @description Sets the filter states based on the query parameters.
   */
  const setFilterStates = () => {
    setPage(queryParams.page ? Number(queryParams.page) : 0);

    // Sync filters to Redux
    dispatch(addFilterSourceRegistryData({
      searchText: queryParams.searchText ?? '',
      status: queryParams.status ?? '',
      fromDate: queryParams.fromDate ?? '',
      toDate: queryParams.toDate ?? ''
    }));
    dispatch(addSortSourceRegistryData({
      sort: queryParams.sort ?? 'updatedTimestamp',
      sortType: queryParams.sortType ?? 'DESC'
    }));
  };

  /**
   * @method sortHandler
   * @description Handles the sorting of the source list.
   * @param label - The label to sort by.
   * @param selectedLabelState - The state of the selected label.
   */
  const sortHandler = (label: string, selectedLabelState: boolean) => {
    setQueryParams((pre): ISourceListQueryParams => ({
      ...pre,
      sort: label,
      sortType: selectedLabelState ? 'ASC' : 'DESC'
    }));
  };

  /**
   * @method handleSortClick
   * @description Handles the click event for sorting the source list.
   * @param e - The click event.
   * @param label - The label of the sort field.
   */
  const handleSortClick = (e: React.MouseEvent<HTMLElement>, label: string) => {
    if (selectedSortField === label) {
      setSelectedSortFieldOrder(() => {
        sortHandler(selectedSortField, !selectedSortFieldOrder);
        return !selectedSortFieldOrder;
      });
    } else {
      setSelectedSortField(label);
      setSelectedSortFieldOrder(true);
      sortHandler(label, true);
    }
  };

  /**
   * @method navigateToSourceRegistryView
  * @description To navigate to view source screen
  * @param name
  */
  const navigateToSourceRegistryView = (name: string, version: number) => {
    navigate(`${URL_REGISTRY.VIEW_SOURCE_REGISTRY}/${name}/${version}`, { state: { path: route.search } });
  };

  /**
   * @method navigateToSourceRegistryEdit
  * @description To navigate to edit source screen
  * @param name
  */
  const navigateToSourceRegistryEdit = (name: string, version: number) => {
    navigate({ pathname: `${URL_REGISTRY.EDIT_SOURCE_REGISTRY}/${name}/${version}` }, { state: { path: route.search, edit: true } });
  };

  const onDeleteSuccess = () => {
    onGetItemsHandler(0);
  };

  /**
* method to populate a single source status data cell jsx with data
* @param row source data
* @returns source status column data cell jsx element
*/
  const renderSourceStatus = (row: ISourceData) => (
    <div>
      <CIStatusCapsule status={row.status} />
    </div>
  );

  /**
   * @method renderColAction
  * @description To render column action buttons
  * @param row
  * @returns JSX element
  */
  const renderColAction = (row: ISourceData) => (
    <div className={classes['action-col']}>
      <CIActionBtnGroup buttons={[{ label: 'view', key: 'viewIcon', onClick: () => navigateToSourceRegistryView(row.sourceName, row.version), testId: 'view-btn' }, { label: 'edit', key: 'editIcon', onClick: () => navigateToSourceRegistryEdit(row.sourceName, row.version), disabled: row.status?.toLocaleUpperCase() === STATUS_TYPES.INACTIVE, testId: 'edit-btn', show: CIActionGuard([requiredAuthPermission.CP.edit], permissionObject.permission) }]} />
      <DropdownMenuButton status={row.status} deleteURL={`sources/${row.sourceName}/version/${row.version}`} onDeleteSuccess={onDeleteSuccess} showOn="list" registryType={REGISTRY_TYPE.SOURCE} />
    </div>
  );

  /**
  * To render Identifier badge view
  * @param row
  * @returns JSX element
  */
  const renderIdentifierAction = (row: ISourceData) => (
    <SourceIdentifierComp sourceData={row.identifier} />
  );

  /**
  * To convert updated time stamp
  * @param row
  * @returns JSX element
  */
  const renderTimeStampAction = (row: ISourceData) => (
    <div className={classes['src-updatedon-lbl']} title={`${toLocalDate({ date: row.updatedTimestamp })} By ${row.updatedBy}`}>{toLocalDate({ date: row.updatedTimestamp })} By {row.updatedBy} </div>
  );

  /**
  * To render source name in tooltip
  * @param row
  * @returns JSX element
  */
  const renderSourceName = (row: ISourceData) => (
    <div className={classes['source-name-wrapper']}>
      <span className={classes['cp-capsule-wrapped-title']} title={row.sourceName}>
        {row.sourceName}
      </span>
      <p className={classes['event-version']}>
        {translate('event_list_component.txt_version', {
          replace: {
            version: row.version
          }
        })}
      </p>

    </div>
  );

  /**
   * @method renderEventName
   * @description Renders the event name for a row in the source list.
   * @param row - The data for the row.
   * @returns The JSX element representing the event name.
   */
  const renderEventName = (row: ISourceData) => (
    <div className={classes['source-name-wrapper']}>
      <span className={classes['cp-capsule-wrapped-title']} title={row.eventName}>
        {row.eventName}
      </span>
      {row?.eventName && (
        <p className={classes['event-version']}>
          {translate('event_list_component.txt_version', {
            replace: {
              version: row.eventVersion
            }
          })}
        </p>
      )}
    </div>
  );

  const footerOptionsObj = {
    itemsPerPage: item,
    currentPageNumber: sourceData.currentPageNumber,
    numberOfPages: sourceData.numberOfPages,
    onCurrentPageNumberChange: onGetPageHandler,
    onItemsPerPageChange: onGetItemsHandler
  };

  const footerObj = { footerType: 'paging', footerOptions: footerOptionsObj };

  /**
   * Handles the click event when the "Create Source Registry" button is clicked.
   */
  const onCreateSourceRegistryBtnClick = () => {
    history(URL_REGISTRY.CREATE_SOURCE_REGISTRY);
  };

  useEffect(() => {
    if (page >= 0) {
      const requestBody = {
        pageNo: page,
        item
      };
      const reqValidity = registryListSchema.validate(requestBody);
      if (reqValidity?.error) {
        throw new Error(reqValidity?.error?.details[0]?.message);
      } else {
        const queryBody = queryParamModifier(queryParams);
        dispatch(fetchSourceRegistryListRequest(queryBody.page, queryBody.item, queryBody.filterData, queryBody.sort));
      }
    }
  }, [queryParams]);

  // setting query Params in url
  useEffect(() => {
    if (Object.keys(queryParams).length !== 0) {
      const searchParams = new URLSearchParams();
      Object.entries(queryParams).forEach(([k, v]) => {
        if (v) {
          searchParams.set(k, v);
        }
      });

      setFilterStates();
      const queryString = searchParams.toString();
      navigate(
        queryString ? `?${queryString}` : route.search,
        { state: route.search }
      );
    }
  }, [queryParams]);

  useEffect(() => {
    if (queryParams.item) {
      persistPageSize(Number(queryParams.item));
    }
  }, [queryParams.item]);

  const debounceHandler = useCallback(debounce((value: string) => {
    dispatch(fetchSourceRegistryListRequest(0, item, { searchText: value, fromDate: queryParams?.fromDate, toDate: queryParams?.toDate }));
  }, debounceDelay), [searchTemp]);

  const handleSearchText = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTemp((event.target.value).trim());
    debounceHandler((event.target.value).trim());
    setQueryParams((pre): ISourceListQueryParams => ({
      ...pre,
      searchText: event.target.value,
      page: ''
    }));
  };

  const isShowingDefaultFilter = useMemo(
    () => {
      const filterStatusList: string[] = queryParams?.status ? queryParams.status.split(',').filter(Boolean) : [];
      const defaultSet = new Set(defaultSourceListStatusArray);
      const isDefaultStatuses = filterStatusList.length === defaultSourceListStatusArray.length && filterStatusList.every((s) => defaultSet.has(s));
      return isDefaultStatuses && !queryParams?.fromDate && !queryParams?.toDate;
    },
    [queryParams]
  );

  const appliedFilterData: CIAppliedFilter[] = useMemo(() => {
    const data: CIAppliedFilter[] = [];
    if (!queryParams) {
      return [];
    }
    const filterStatusList: string[] = (queryParams?.status?.split(',') ?? []).filter(Boolean);

    if (filterStatusList.length > 0 && filterStatusList.length < allStatusList.length) {
      filterStatusList.forEach((f: string) =>
        data.push({ text: allStatusMap[f], type: APPPLIED_FILTER.STATUS, value: f }));
    }
    if (queryParams.fromDate && queryParams.toDate) {
      data.push({ value: queryParams.toDate as string, type: 'date', text: `${translate('fsmList.last-modified')}: ${toLocalDate({ date: queryParams.fromDate, dateTimeFormat: { date: true } })} - ${toLocalDate({ date: queryParams.toDate, dateTimeFormat: { date: true } })}` });
    } else if (queryParams.fromDate) {
      data.push({ value: queryParams.fromDate as string, type: 'start_date', text: `${translate('fsmList.last-modified-from')}: ${toLocalDate({ date: queryParams.fromDate, dateTimeFormat: { date: true } })}` });
    } else if (queryParams.toDate) {
      data.push({ value: queryParams.toDate as string, type: 'end_date', text: `${translate('fsmList.last-modified-to')}: ${toLocalDate({ date: queryParams.toDate, dateTimeFormat: { date: true } })}` });
    }
    return data;
  }, [queryParams]);

  const appliedFilterChangeHandler = (filters: CIAppliedFilter[]) => {
    let hasStartDate = false;
    let hasEndDate = false;
    const statusList: string[] = [];

    filters.forEach((f) => {
      if (f.type === APPPLIED_FILTER.START_DATE) {
        hasStartDate = true;
      } else if (f.type === APPPLIED_FILTER.END_DATE) {
        hasEndDate = true;
      } else if (f.type === APPPLIED_FILTER.DATE) {
        hasStartDate = true;
        hasEndDate = true;
      } else if (f.type === APPPLIED_FILTER.STATUS) {
        statusList.push(f.value);
      }
    });

    setPage(0);
    setQueryParams((pre): ISourceListQueryParams => ({
      ...pre,
      searchText: searchTemp ?? '',
      fromDate: hasStartDate ? (queryParams?.fromDate ?? '') : '',
      toDate: hasEndDate ? (queryParams?.toDate ?? '') : '',
      status: statusList.join(','),
      page: '0'
    }));
  };

  return (
    <div className={classes['sr-list-container']}>
      <Loader show={isLoading} />
      <div className={classes.filters}>
        <div className={classes['search-filter-div']}>
          <div className={classes['search-div']}>
            <RegistriesFilter
              clearFunction={resetFiltersHandler}
              setQueryParams={setQueryParams}
              queryParams={queryParams}
            />
            <div className={classes.search}>
              <SearchInput
                label="search"
                type="search"
                placeholder={String(translate('sourceList.search_plchd'))}
                onChange={handleSearchText}
                data-testid="filterSearchInput"
                value={searchTemp}
              />
            </div>
          </div>
          {CIActionGuard([requiredAuthPermission.CP.edit], permissionObject.permission) && (
            <Button variant="primary" onClick={onCreateSourceRegistryBtnClick} data-testid="src-reg-btn">
              {translate('registry_view.source_registry_btn')}
            </Button>
          )}
        </div>
        <CIAppliedListFilter
          className={classes['applied-filters']}
          totalRecords={sourceData.totalRecords}
          appliedFilters={appliedFilterData}
          onFilterChange={appliedFilterChangeHandler}
          onResetFilters={resetFiltersHandler}
          showResetFiltersButton={!isShowingDefaultFilter}
        />
      </div>

      <div className={classes['source-list']} data-testid="sr-list">
        {(listSourceRegistryErrorMessage !== '' && !isLoading) && (
          <div className={classes.page}>
            <UnableToFetch />
          </div>
        )}
        {(listSourceRegistryErrorMessage === '' && !isLoading && !sourceData.sources?.length) && (
          <div className={classes.page}>
            <NoDataAvailable />
          </div>
        )}
        {renderListCondition && (
          <CITableGrid
            headers={[
              {
                label: translate('sourceList.sourceName'),
                name: 'sourceName',
                sortOptions: {
                  callback: handleSortClick
                },
                options: { breakPointWidth: 2 },
                columnData: { field: 'sourceName', render: renderSourceName }
              },
              {
                name: 'status',
                label: translate('sourceList.status'),
                sortOptions: {
                  callback: handleSortClick
                },
                options: { breakPointWidth: 1 },
                columnData: {
                  field: 'status', render: renderSourceStatus
                }
              },
              {
                label: translate('event_list_component.lbl_event_name'),
                name: 'eventName',
                options: { breakPointWidth: 2 },
                columnData: { field: 'eventName', render: renderEventName }
              },
              {
                label: translate('source_event_registry.ser_lbl_key_idn'),
                name: 'identifier',
                sortOptions: {
                  callback: handleSortClick
                },
                options: { breakPointWidth: 3 },
                columnData: { field: 'identifier', render: renderIdentifierAction }
              },
              {
                label: translate('sourceList.lastModified'),
                labelOptions: { helperText: `(${getLocaleDateTimeFormat()})`, helperTextClass: 'updatedon-sublabel' },
                name: 'updatedTimestamp',
                sortOptions: {
                  callback: handleSortClick
                },
                options: { breakPointWidth: 3 },
                columnData: { field: 'updatedTimestamp', render: renderTimeStampAction }
              },
              {
                label: '',
                name: 'action',
                options: { breakPointWidth: 1 },
                columnData: { field: 'action', render: renderColAction, className: 'render-action' }
              }
            ]}
            sorted={sortedObj}
            tableData={sourceData.sources}
            footer={footerObj}
          />
        )}
      </div>
    </div>
  );
};

/**
* Component to render tooltip msg in list format
*/
const PopOverMsgComp = ({ sourceData }: ISourceIdentifierComp) => (
  <ol className={classes['msg-body']}>
    {sourceData?.split(',')?.map((item: string) => (
      <li key={item}>{item}</li>
    ))}
  </ol>
);

/**
* Component to render Source Identifier component
*/
const SourceIdentifierComp = ({ sourceData }: ISourceIdentifierComp) => (
  <div className={classes['source-idn-wrapper']}>
    <span className={classes['src-idn-items']}>
      {sourceData.split(',')?.map((item: string, index: number) => {
        if (index < 2) {
          return (
            <span key={`${item + index}`} className={classes['source-identifier-label']} data-testid="sr-list-capsule">
              <CapsuleButton
                label={item}
                onClickHandler={() => { }}
                deleteIcon={false}
                ellipseLength={15}
              />
            </span>
          );
        }
        return null;
      })}
      <CITooltip placement="right" message={<PopOverMsgComp sourceData={sourceData} />}>
        <span>
          {sourceData.split(',').length - 2 > 0 ? <CircleBadge count={`+${sourceData.split(',').length - 2}`} /> : null}
        </span>
      </CITooltip>
    </span>
  </div>
);

export default SourceList;
